import {
  getCustomAttributeString,
  shippingDaysFromCustomAttributes,
  type ActionBarCrossSellCandidateProduct,
  type ActionBarCrossSellVariant
} from '~/utils/shop/selectActionBarCrossSellProduct';
import { isDefined } from '~/utils/guards/isDefined';
import { PRODUCT_IMAGE } from '~/utils/shop/constants';

type AttrItem = {
  __typename?: string;
  code?: string | null;
  value?: string | null;
} | null;

type AttrBlock = {
  items?: ReadonlyArray<AttrItem> | null;
} | null;

type GqlVariantProduct = {
  sku?: string | null;
  name?: string | null;
  price_range?: {
    minimum_price?: {
      final_price?: {
        value?: number | null;
        currency?: string | null;
      } | null;
    } | null;
  } | null;
  custom_attributesV2?: AttrBlock;
  listing_attributes?: AttrBlock;
} | null;

type GqlConfigurableOption = {
  attribute_code?: string | null;
  values?: ReadonlyArray<{
    label?: string | null;
    uid?: string | null;
  } | null> | null;
} | null;

type GqlVariant = {
  attributes?: ReadonlyArray<{
    code?: string | null;
    uid?: string | null;
  } | null> | null;
  product?: GqlVariantProduct;
} | null;

type GqlParentProduct = {
  sku?: string | null;
  configurable_options?: ReadonlyArray<GqlConfigurableOption> | null;
  variants?: ReadonlyArray<GqlVariant> | null;
} | null;

export type ProductsWithParentVariantsItem = {
  sku?: string | null;
  name?: string | null;
  price_range?: {
    minimum_price?: {
      final_price?: {
        value?: number | null;
        currency?: string | null;
      } | null;
    } | null;
  } | null;
  custom_attributesV2?: AttrBlock;
  listing_attributes?: AttrBlock;
  parent_products?: ReadonlyArray<GqlParentProduct> | null;
};

function mergeAttrItems(...blocks: Array<AttrBlock | undefined>): AttrItem[] {
  const byCode = new Map<string, AttrItem>();
  for (const block of blocks) {
    for (const item of block?.items ?? []) {
      if (item?.code) {
        byCode.set(item.code, item);
      }
    }
  }
  return [...byCode.values()];
}

function lengthLabelFromVariant(
  variant: GqlVariant,
  options: ReadonlyArray<GqlConfigurableOption> | null | undefined,
  productName: string | null | undefined
): string {
  for (const attr of variant?.attributes ?? []) {
    if (!attr?.code || !attr.uid) {
      continue;
    }
    const option = options?.find(
      (entry) => entry?.attribute_code === attr.code
    );
    const value = option?.values?.find((entry) => entry?.uid === attr.uid);
    if (value?.label) {
      return value.label;
    }
  }

  const match = /\b(\d+(?:[.,]\d+)?\s*m)\b/i.exec(productName ?? '');
  return match?.[1] ?? '';
}

function mapVariant(
  variant: GqlVariant,
  options: ReadonlyArray<GqlConfigurableOption> | null | undefined,
  fallbackAttrs?: AttrItem[]
): ActionBarCrossSellVariant | null {
  const product = variant?.product;
  const sku = product?.sku;
  if (!sku) {
    return null;
  }

  const attrs = mergeAttrItems(
    product.custom_attributesV2,
    product.listing_attributes
  );
  const mergedWithFallback =
    fallbackAttrs && fallbackAttrs.length > 0
      ? mergeAttrItems({ items: attrs }, { items: fallbackAttrs })
      : attrs;

  return {
    sku,
    name: product.name,
    lengthLabel: lengthLabelFromVariant(variant, options, product.name),
    priceValue: product.price_range?.minimum_price?.final_price?.value ?? null,
    currency: product.price_range?.minimum_price?.final_price?.currency ?? null,
    shippingTimeDays: shippingDaysFromCustomAttributes(mergedWithFallback),
    imageJson: getCustomAttributeString(mergedWithFallback, PRODUCT_IMAGE)
  };
}

/**
 * Builds ordered candidates for one recommended-product SKU using the batch
 * parent-variants response keyed by SKU.
 */
export function buildCandidatesForRecommendedSku(
  recommendedSku: string,
  productsBySku: ReadonlyMap<string, ProductsWithParentVariantsItem>
): ActionBarCrossSellCandidateProduct | null {
  const product = productsBySku.get(recommendedSku);
  if (!product) {
    return null;
  }

  const fallbackAttrs = mergeAttrItems(
    product.custom_attributesV2,
    product.listing_attributes
  );

  const parent = product.parent_products?.[0];
  const variants =
    parent?.variants
      ?.map((variant) =>
        mapVariant(variant, parent.configurable_options, fallbackAttrs)
      )
      .filter(isDefined) ?? [];

  if (variants.length > 0) {
    return {
      sku: recommendedSku,
      variants
    };
  }

  const selfVariant: ActionBarCrossSellVariant = {
    sku: recommendedSku,
    name: product.name,
    lengthLabel: '',
    priceValue: product.price_range?.minimum_price?.final_price?.value ?? null,
    currency: product.price_range?.minimum_price?.final_price?.currency ?? null,
    shippingTimeDays: shippingDaysFromCustomAttributes(fallbackAttrs),
    imageJson: getCustomAttributeString(fallbackAttrs, PRODUCT_IMAGE)
  };

  return {
    sku: recommendedSku,
    variants: [selfVariant]
  };
}

export function indexProductsBySku(
  items: ReadonlyArray<ProductsWithParentVariantsItem | null | undefined>
): Map<string, ProductsWithParentVariantsItem> {
  const map = new Map<string, ProductsWithParentVariantsItem>();
  for (const item of items) {
    if (item?.sku) {
      map.set(item.sku, item);
    }
  }
  return map;
}

/**
 * Merge cart + listing custom attributes so DecoratedProduct can resolve images.
 */
export function flattenProductAttrsForDecoratedBuild(
  item: ProductsWithParentVariantsItem
): ProductsWithParentVariantsItem {
  const merged = mergeAttrItems(
    item.custom_attributesV2,
    item.listing_attributes
  );
  return {
    ...item,
    custom_attributesV2: { items: merged }
  };
}
